import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  AlertTriangle, Calendar, Clock, MapPin, User, RefreshCw, 
  UserX, Send, ChevronDown, ChevronUp, DollarSign, X, Check,
  Archive, MessageSquare, Trash2
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { jobsAPI, workersAPI, notesAPI, archiveAPI } from '../../services/api';

const DeclinedJobsTab = ({ setNotesModal }) => {
  const [declinedJobs, setDeclinedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJob, setExpandedJob] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [reassignModal, setReassignModal] = useState(null);
  const [selectedWorker, setSelectedWorker] = useState('');
  const [processing, setProcessing] = useState(false);
  const [noteCounts, setNoteCounts] = useState({});

  useEffect(() => {
    fetchDeclinedJobs();
    fetchWorkers();
  }, []);

  const fetchDeclinedJobs = async () => {
    setLoading(true);
    try {
      const res = await jobsAPI.getDeclinedJobs();
      setDeclinedJobs(res.data || []);
    } catch (err) {
      console.error('Failed to fetch declined jobs:', err);
      toast.error('Failed to load declined jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkers = async () => {
    try {
      const res = await workersAPI.getAll();
      setWorkers(res.data?.filter(w => w.status === 'active') || []);
    } catch (err) {
      console.error('Failed to fetch workers:', err);
    }
  };

  const handleCancelJob = async (job) => {
    if (!window.confirm(`Cancel this job for ${job.customer_name}? This will notify the customer.`)) return;
    setProcessing(true);
    try {
      await jobsAPI.updateStatus(job.id, 'cancelled');
      toast.success('Job cancelled');
      fetchDeclinedJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel job');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteJob = async (job) => {
    if (!window.confirm(`Permanently delete this declined job? This cannot be undone.`)) return;
    setProcessing(true);
    try {
      await jobsAPI.delete(job.id);
      toast.success('Job deleted');
      fetchDeclinedJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete job');
    } finally {
      setProcessing(false);
    }
  };

  const handleArchive = async (job) => {
    if (!window.confirm('Archive this declined job? You can restore it later from the Archive tab.')) return;
    setProcessing(true);
    try {
      await archiveAPI.archiveJob(job.id);
      toast.success('Job archived');
      fetchDeclinedJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive');
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenNotes = (job) => {
    if (setNotesModal) {
      setNotesModal({
        type: 'declined_job',
        id: job.id,
        title: `Notes: ${job.service_name}`,
        subtitle: `${job.customer_name} - ${job.scheduled_date}`
      });
    } else {
      toast.info('Notes feature coming soon');
    }
  };

  const handlePostToJobBoard = async (jobId) => {
    setProcessing(true);
    try {
      await jobsAPI.postJob(jobId);
      toast.success('Job posted to job board for workers to claim');
      await fetchDeclinedJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post job');
    } finally {
      setProcessing(false);
    }
  };

  const handleReassign = async () => {
    if (!selectedWorker || !reassignModal) return;
    
    setProcessing(true);
    try {
      await jobsAPI.assignJob(reassignModal.id, { worker_id: selectedWorker });
      toast.success('Job reassigned successfully');
      setReassignModal(null);
      setSelectedWorker('');
      await fetchDeclinedJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reassign job');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <UserX className="w-6 h-6 text-[var(--error)]" />
            Declined Jobs
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Jobs that workers have declined - review reasons and reassign or post to job board
          </p>
        </div>
        <Button onClick={fetchDeclinedJobs} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      {declinedJobs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card p-4 bg-[var(--error)]/10 border-[var(--error)]/30">
            <p className="text-sm text-[var(--error)]">Total Declined</p>
            <p className="text-2xl font-bold text-red-700">{declinedJobs.length}</p>
          </div>
          <div className="card p-4 bg-[var(--warning)]/10 border-[var(--warning)]/30">
            <p className="text-sm text-amber-600">Awaiting Action</p>
            <p className="text-2xl font-bold text-amber-700">
              {declinedJobs.filter(j => j.status === 'available' && !j.worker_id).length}
            </p>
          </div>
          <div className="card p-4 bg-[var(--success)]/10 border-[var(--success)]/30">
            <p className="text-sm text-[var(--success)]">Reassigned</p>
            <p className="text-2xl font-bold text-green-700">
              {declinedJobs.filter(j => j.worker_id).length}
            </p>
          </div>
        </div>
      )}

      {/* Jobs List */}
      {declinedJobs.length === 0 ? (
        <div className="card p-12 text-center">
          <Check className="w-16 h-16 text-[var(--success)] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-green-700">No Declined Jobs</h3>
          <p className="text-[var(--text-muted)] mt-2">
            Great news! All assigned jobs have been accepted by workers.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {declinedJobs.map(job => (
            <div 
              key={job.id} 
              className="card overflow-hidden"
              data-testid={`declined-job-${job.id}`}
            >
              {/* Job Header */}
              <div className="p-4 bg-[var(--error)]/10 border-b border-red-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-lg">{job.service_name}</h3>
                      <span className="badge-error text-xs">
                        {job.decline_count}x Declined
                      </span>
                      {job.status === 'available' && !job.worker_id && (
                        <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">
                          Needs Attention
                        </span>
                      )}
                      {job.worker_id && (
                        <span className="badge-success text-xs">
                          Reassigned
                        </span>
                      )}
                      {job.job_posted && !job.worker_id && (
                        <span className="bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-1 rounded-full text-xs font-medium">
                          On Job Board
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-[var(--text-muted)]">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {job.scheduled_date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {job.scheduled_time}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {job.city}, {job.state}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {job.customer_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        ${Number(job.total_amount || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!job.worker_id && (
                      <>
                        {!job.job_posted && (
                          <Button
                            onClick={() => handlePostToJobBoard(job.id)}
                            disabled={processing}
                            variant="outline"
                            size="sm"
                            data-testid={`post-job-btn-${job.id}`}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Post to Board
                          </Button>
                        )}
                        <Button
                          onClick={() => setReassignModal(job)}
                          disabled={processing}
                          className="bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white"
                          size="sm"
                          data-testid={`reassign-btn-${job.id}`}
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Reassign
                        </Button>
                      </>
                    )}
                    <Button
                      onClick={() => handleOpenNotes(job)}
                      variant="ghost"
                      size="sm"
                      title="Notes"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleCancelJob(job)}
                      disabled={processing}
                      variant="ghost"
                      size="sm"
                      className="text-amber-600 hover:text-amber-700"
                      title="Cancel Job"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleArchive(job)}
                      disabled={processing}
                      variant="ghost"
                      size="sm"
                      className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                      title="Archive"
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDeleteJob(job)}
                      disabled={processing}
                      variant="ghost"
                      size="sm"
                      className="text-[var(--error)] hover:text-red-700"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                      variant="ghost"
                      size="sm"
                    >
                      {expandedJob === job.id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Decline History (Expanded) */}
              {expandedJob === job.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="p-4 bg-[var(--background-paper)]"
                >
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-[var(--error)]" />
                    Decline History ({job.decline_count} total)
                  </h4>
                  <div className="space-y-3">
                    {job.decline_history?.map((decline) => (
                      <div 
                        key={`${decline.worker_id || decline.worker_name}-${decline.timestamp || decline.declined_at}`} 
                        className="bg-[var(--error)]/10 border border-red-100 rounded-lg p-3"
                        data-testid={`decline-reason-${job.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-[var(--error)]">
                              {decline.worker_name || 'Worker'}
                            </p>
                            <p className="text-sm text-[var(--error)] mt-1">
                              <strong>Reason:</strong> {decline.reason || 'No reason provided'}
                            </p>
                          </div>
                          <span className="text-xs text-red-400">
                            {formatDateTime(decline.declined_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Current Status */}
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm">
                      <strong>Current Status:</strong>{' '}
                      <span className="capitalize">{job.status?.replace('_', ' ')}</span>
                    </p>
                    {job.worker_id && (
                      <p className="text-sm mt-1">
                        <strong>Currently Assigned To:</strong> {job.worker_name || 'Worker'}
                      </p>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reassign Modal */}
      {reassignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-2xl shadow-2xl w-full max-w-md p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Reassign Job</h3>
              <button 
                onClick={() => {
                  setReassignModal(null);
                  setSelectedWorker('');
                }}
                className="p-1 hover:bg-[var(--background-subtle)] rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-[var(--background-subtle)] rounded-lg">
              <p className="font-medium">{reassignModal.service_name}</p>
              <p className="text-sm text-[var(--text-muted)]">
                {reassignModal.scheduled_date} at {reassignModal.scheduled_time}
              </p>
              <p className="text-sm text-[var(--text-muted)]">
                {reassignModal.customer_name} - {reassignModal.city}, {reassignModal.state}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Select Worker</label>
              <select
                value={selectedWorker}
                onChange={(e) => setSelectedWorker(e.target.value)}
                className="w-full p-3 border border-[var(--border-primary)] rounded-lg bg-[var(--background-subtle)] text-[var(--text-primary)]"
                data-testid="reassign-worker-select"
              >
                <option value="">Choose a worker...</option>
                {workers
                  .filter(w => {
                    // Filter out workers who already declined this job
                    const declinedWorkerIds = reassignModal.decline_history?.map(d => d.worker_id) || [];
                    return !declinedWorkerIds.includes(w.id);
                  })
                  .map(worker => (
                    <option key={worker.id} value={worker.id}>
                      {worker.full_name} - {worker.service_types?.join(', ') || 'All Services'}
                    </option>
                  ))
                }
              </select>
              {reassignModal.decline_count > 0 && (
                <p className="text-xs text-amber-600 mt-2">
                  Note: Workers who previously declined are not shown
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setReassignModal(null);
                  setSelectedWorker('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReassign}
                disabled={!selectedWorker || processing}
                className="flex-1 btn-primary"
                data-testid="confirm-reassign-btn"
              >
                {processing ? 'Reassigning...' : 'Reassign Job'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default DeclinedJobsTab;
